// src/components/DesktopNotificationOverlay.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Slide,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import notificationService from '../services/notificationService';

/**
 * Desktop notification overlay component
 * Shows notifications in a non-intrusive overlay at the top-right of the screen
 * Similar to Outlook new email notifications
 */
const DesktopNotificationOverlay = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if running as PWA (optional - we'll show notifications either way)
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone ||
                          document.referrer.includes('android-app://');
      setIsPWA(isStandalone);
      //console.log('[Desktop Overlay] PWA mode:', isStandalone);
    };

    checkPWA();

    // Listen for push notifications from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Poll for new notifications as fallback (reduced frequency for browser)
    const pollInterval = setInterval(() => {
      checkForNewNotifications();
    }, 60000); // Every 60 seconds

    // Listen for test notifications from the admin panel
    const handleTestNotification = (event) => {
      //console.log('[Desktop Overlay] Test notification received:', event.detail);
      showNotification(event.detail);
    };

    window.addEventListener('test-notification', handleTestNotification);

    // Initial check
    checkForNewNotifications();

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      window.removeEventListener('test-notification', handleTestNotification);
      clearInterval(pollInterval);
    };
  }, []);

  const handleServiceWorkerMessage = (event) => {
    //console.log('[Desktop Overlay] Message from service worker:', event.data);

    switch (event.data.type) {
      case 'NOTIFICATION_CLICKED':
        // Handle notification click - navigate and close overlay
        if (event.data.actionUrl) {
          navigate(event.data.actionUrl);
        }
        break;

      case 'PLAY_CRITICAL_SOUND':
        // Play critical alert sound
        playCriticalSound();
        break;

      case 'NEW_NOTIFICATION':
        // Show new notification in overlay
        showNotification(event.data.notification);
        break;

      default:
        break;
    }
  };

  const checkForNewNotifications = async () => {
    try {
      const response = await notificationService.getNotifications({
        limit: 1,
        includeRead: false
      });

      if (response.notifications && response.notifications.length > 0) {
        const latestNotification = response.notifications[0];

        // Check if we already showed this notification
        const alreadyShown = notifications.some(n => n.id === latestNotification.id);
        if (!alreadyShown) {
          showNotification(latestNotification);
        }
      }
    } catch (error) {
      console.error('[Desktop Overlay] Failed to check notifications:', error);
    }
  };

  const showNotification = (notification) => {
    // Add notification to display queue
    setNotifications(prev => [...prev, { ...notification, timestamp: Date.now() }]);

    // Auto-dismiss after 10 seconds (unless it's critical)
    const dismissTimeout = notification.priority === 'critical' || notification.priority === 'urgent'
      ? 30000  // 30 seconds for critical
      : 10000; // 10 seconds for others

    setTimeout(() => {
      dismissNotification(notification.id);
    }, dismissTimeout);

    // Play sound for critical notifications
    if (notification.priority === 'critical' ||
        notification.priority === 'urgent' ||
        notification.overrideMute) {
      playCriticalSound();
    }
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await notificationService.markAsRead(notification.id);
    } catch (error) {
      console.error('[Desktop Overlay] Failed to mark as read:', error);
    }

    // Navigate if action URL exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    } else {
      navigate('/notifications');
    }

    // Dismiss the notification
    dismissNotification(notification.id);
  };

  const playCriticalSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 880; // A5 note
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      //console.log('[Desktop Overlay] Critical alert sound played');
    } catch (error) {
      console.error('[Desktop Overlay] Failed to play sound:', error);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
      case 'urgent':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'high':
        return <WarningIcon sx={{ color: '#ff9800' }} />;
      case 'normal':
        return <InfoIcon sx={{ color: '#2196f3' }} />;
      case 'low':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      default:
        return <InfoIcon sx={{ color: '#2196f3' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
      case 'urgent':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'normal':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  // Always render (show notifications in browser and PWA)
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80, // Below the app bar
        right: 16,
        zIndex: 99999, // Very high z-index to be on top of everything
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 400,
        pointerEvents: 'none'
      }}
    >
      {notifications.map((notification, index) => (
        <Slide
          key={notification.id}
          direction="left"
          in={true}
          mountOnEnter
          unmountOnExit
        >
          <Paper
            elevation={8}
            sx={{
              p: 2,
              cursor: 'pointer',
              pointerEvents: 'auto',
              borderLeft: `4px solid ${getPriorityColor(notification.priority)}`,
              backgroundColor: 'background.paper',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 12
              }
            }}
            onClick={() => handleNotificationClick(notification)}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              {/* Icon */}
              <Avatar
                sx={{
                  bgcolor: 'transparent',
                  width: 40,
                  height: 40
                }}
              >
                {getPriorityIcon(notification.priority)}
              </Avatar>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {notification.title || 'Notification'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {notification.body || notification.message || 'You have a new notification'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.created_at
                    ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                    : 'Just now'}
                </Typography>
              </Box>

              {/* Close button */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissNotification(notification.id);
                }}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Slide>
      ))}
    </Box>
  );
};

export default DesktopNotificationOverlay;
